/**
 * Nigeria map — REAL geographic projection using d3-geo + actual state GeoJSON.
 * Source: GADM 4.1 Nigeria admin-1 boundaries, simplified ~95% with shapely (0.015° tolerance).
 * 37 features (36 states + FCT).
 *
 * Bubbles are positioned at d3.geoCentroid() of each state polygon — no hard-coded coordinates.
 */
import { useMemo, useState } from "react";
import { geoMercator, geoPath, geoCentroid } from "d3-geo";
import { scaleSqrt } from "d3-scale";
import geoData from "../data/ng-states.json";

export function NigeriaMap({ data = [], height = 380, onStateClick }) {
  const [hoverName, setHoverName] = useState(null);

  const lookup = useMemo(() => Object.fromEntries(data.map(d => [d.state, d.count || 0])), [data]);
  const maxCount = Math.max(...data.map(d => d.count || 0), 1);

  // Aliases: GADM names may differ slightly from our seeded state names
  const alias = {
    "Abuja Federal Capital Territory": "FCT",
    "Federal Capital Territory": "FCT",
    "Nassarawa": "Nasarawa",
  };
  const stateKey = (name) => alias[name] || name;

  const width = 1000;
  const h = 760;
  const projection = useMemo(() => geoMercator().fitSize([width - 20, h - 20], geoData), []);
  const pathGen = useMemo(() => geoPath(projection), [projection]);
  const radius = useMemo(() => scaleSqrt().domain([0, maxCount]).range([4, 28]), [maxCount]);

  const features = geoData.features;

  // Pre-compute centroids in projected pixel space
  const centroids = useMemo(() => features.map(f => {
    const [lng, lat] = geoCentroid(f);
    const [x, y] = projection([lng, lat]) || [0, 0];
    return { name: stateKey(f.properties.name), x, y, count: lookup[stateKey(f.properties.name)] || 0 };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [features, projection, lookup]);

  const colorFor = (count) => {
    if (!count) return "#F5F4EF";
    const t = count / maxCount;
    if (t > 0.7) return "#C2410C";
    if (t > 0.45) return "#E16A2A";
    if (t > 0.25) return "#F59A2D";
    return "#FCE5C9";
  };

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${h}`} width="100%" style={{ height }} className="select-none">
        <defs>
          <radialGradient id="ngBgGlow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#F59A2D" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#F59A2D" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width={width} height={h} fill="url(#ngBgGlow)" />

        {/* Thick national outline — drawn underneath the states as a halo */}
        <g pointerEvents="none">
          {features.map((f, i) => (
            <path
              key={`halo-${i}`}
              d={pathGen(f) || ""}
              fill="none"
              stroke="#0F172A"
              strokeWidth={3.5}
              strokeLinejoin="round"
              opacity={0.85}
            />
          ))}
        </g>

        {/* State polygons */}
        <g>
          {features.map((f, i) => {
            const name = stateKey(f.properties.name);
            const isHover = hoverName === name;
            const count = lookup[name] || 0;
            const fill = isHover ? "#FAEED7" : "#FFFFFF";
            return (
              <path
                key={i}
                d={pathGen(f) || ""}
                fill={fill}
                stroke={isHover ? "#0F172A" : "#94A3B8"}
                strokeWidth={isHover ? 2 : 1}
                strokeLinejoin="round"
                onMouseEnter={() => setHoverName(name)}
                onMouseLeave={() => setHoverName(null)}
                onClick={() => onStateClick && onStateClick(name)}
                style={{ cursor: "pointer", transition: "fill 0.15s, stroke 0.15s" }}
                data-testid={`map-state-${name}`}
              />
            );
          })}
        </g>

        {/* Bubbles at computed centroids */}
        <g>
          {centroids.map((c, i) => {
            if (!c.count) return null;
            const r = radius(c.count);
            const isHover = hoverName === c.name;
            const fill = colorFor(c.count);
            const textColor = c.count > maxCount * 0.4 ? "#FFFFFF" : "#0F172A";
            return (
              <g
                key={i}
                onMouseEnter={() => setHoverName(c.name)}
                onMouseLeave={() => setHoverName(null)}
                onClick={() => onStateClick && onStateClick(c.name)}
                style={{ cursor: "pointer" }}
                data-testid={`bubble-${c.name}`}
              >
                {c.count > maxCount * 0.5 && (
                  <circle cx={c.x} cy={c.y} r={r + 4} fill={fill} opacity="0.3">
                    <animate attributeName="r" values={`${r + 4};${r + 10};${r + 4}`} dur="2.2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.3;0.05;0.3" dur="2.2s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={r}
                  fill={fill}
                  stroke="#FFFFFF"
                  strokeWidth={1.5}
                  style={{ transition: "r 0.15s" }}
                />
                <text x={c.x} y={c.y + 4} textAnchor="middle" fontSize={Math.min(13, 9 + r/4)} fontWeight="500" fill={textColor} pointerEvents="none">
                  {c.count}
                </text>
              </g>
            );
          })}
        </g>

        {/* Labels for top states (or hovered) */}
        <g>
          {centroids.map((c, i) => {
            if (!c.count) return null;
            const show = hoverName === c.name || c.count >= maxCount * 0.5 || ["Lagos","FCT","Rivers","Kano"].includes(c.name);
            if (!show) return null;
            const r = radius(c.count);
            const labelOffsetX = c.name === "Lagos" ? r + 8 : 0;
            const labelOffsetY = c.name === "Lagos" ? 3 : r + 13;
            const anchor = c.name === "Lagos" ? "start" : "middle";
            return (
              <text
                key={`lbl-${i}`}
                x={c.x + labelOffsetX}
                y={c.y + labelOffsetY}
                textAnchor={anchor}
                fontSize="10.5"
                fontWeight="500"
                fill="#0F172A"
                pointerEvents="none"
                style={{ paintOrder: "stroke", stroke: "#FAF9F5", strokeWidth: "3px", strokeLinejoin: "round" }}
              >
                {c.name}
              </text>
            );
          })}
        </g>
      </svg>

      {/* Legend + hover detail */}
      <div className="flex items-center justify-between mt-2 px-2">
        <div className="flex items-center gap-2 text-[10px] text-ink-subtle">
          <span>0</span>
          <div className="flex items-center h-2 rounded-full overflow-hidden">
            {[0.05, 0.25, 0.45, 0.7, 1].map((t, i) => (
              <span key={i} className="w-5 h-2" style={{ background: colorFor(t * maxCount) }} />
            ))}
          </div>
          <span className="tabular">{maxCount}+</span>
          <span className="ml-2 text-ink-subtle/80">alerts · hover a state, click to drill</span>
        </div>
        {hoverName ? (
          <div className="text-[11px] text-ink-muted">
            <span className="font-medium text-ink">{hoverName}</span> · {lookup[hoverName] || 0} alert{(lookup[hoverName] || 0) === 1 ? "" : "s"}
          </div>
        ) : (
          <div className="text-[10px] text-ink-subtle">GADM 4.1 · Mercator projection</div>
        )}
      </div>
    </div>
  );
}

export const NIGERIAN_STATES = geoData.features.map(f => f.properties.name);
